import { Component, OnDestroy, OnInit, Inject } from '@angular/core';
import {
  InvoiceItemInterfaceWithId, AccountingServiceInterfaceWithId,
  InvoiceItemInterface, TaxRateInterfaceWithId, ProjectInterfaceWithId, ProductInterfaceWithId
} from '@sk/sk-interfaces';
import { FormGroup, Validators, FormBuilder, FormControl, AbstractControl } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import * as moment from 'moment';
import { debounceTime, startWith, map, switchMap, takeUntil } from 'rxjs/operators';
import { AutoCompleteValidator } from 'shared/validators/autocomplete-validator';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TaxRatesService } from 'services/tax-rate.service';
import { AcctServicesService } from 'services/acct-services.service';
import { SuppliesService } from 'services/supplies.service';
import { environment } from 'environments/environment';
import { Dictionary } from '@ngrx/entity';
import { ProjectsFacade } from '../../../../../+state/projects/projects.facade';
import firebase from 'firebase/app';


@Component({
  selector: 'sk-invoiceitem-edit-v2-dialog',
  templateUrl: './invoiceitem-edit-dialog-v2.component.html',
  styleUrls: ['./invoiceitem-edit-dialog-v2.component.scss']
})
export class InvoiceitemEditDialogV2Component implements OnInit, OnDestroy {
  invoiceItem: InvoiceItemInterfaceWithId;
  invoiceForm: FormGroup;
  invoiceType = 'Project';
  hourly = false;
  projects: ProjectInterfaceWithId[];
  projectLookup: Dictionary<ProjectInterfaceWithId>;
  filteredProjects: Observable<ProjectInterfaceWithId[]>;
  taxRates$: Observable<TaxRateInterfaceWithId[]>;
  acctServices: AccountingServiceInterfaceWithId[];
  products: ProductInterfaceWithId[];
  editMode = false;
  onReady = false;
  defaultValue = null;

  private unitPrice = 0;
  private invoiceSubtotal = 0;
  private invoiceTotal = 0;
  private salesTaxAmount = 0;

  unsubscribe$ = new Subject();

  constructor(private fb: FormBuilder,
              private snackBar: MatSnackBar,
              private taxRatesService: TaxRatesService,
              private acctServicesService: AcctServicesService,
              private suppliesService: SuppliesService,
              // private projectService: ProjectService,
              private projectsFacade: ProjectsFacade,
              private dialogRef: MatDialogRef<InvoiceitemEditDialogV2Component>,
              @Inject(MAT_DIALOG_DATA) public data: InvoiceItemInterfaceWithId) {
  }

  ngOnInit() {
    this.getData();
    this.editMode = (this.data.id) ? true : false;
    this.invoiceItem = this.data;
    this.taxRates$ = this.taxRatesService.getTaxRates();

    this.initForm();
    // I dont think I will need to do this this.calculateAmounts();
  }

  compareTax(o1, o2): boolean {
    return o1.description === o2.description;
  }

  compareService(o1, o2): boolean {
    return o1.name === o2.name;
  }

  compareProduct(o1, o2): boolean {
    return o1.productName === o2.productName;
  }

  newItemControls() {
    // If this is a brand new invoice item, you need to let the user enter the project,
    // Need to get the project, invoiceType, hourly/flat fee, billing date,
    this.invoiceForm.addControl('project', new FormControl(null, [Validators.required, AutoCompleteValidator.bind(this)]));
    this.filterProjects();
    this.invoiceForm.addControl('invoiceType', new FormControl('Project'));
    this.invoiceForm.addControl('invoiceDate', new FormControl(moment(), [Validators.required]));
    this.invoiceForm.addControl('service', new FormControl(null, Validators.required));
    this.onInvoiceTypeChanges();
  }

  patchForm() {

    this.invoiceForm.patchValue({
      quantity: (this.invoiceItem.quantity) ? this.invoiceItem.quantity : 1,
      unitPrice: (this.invoiceItem.unitPrice) ? this.invoiceItem.unitPrice : 0.00,
      salesTaxRate: (this.invoiceItem.salesTaxRate) ? this.invoiceItem.salesTaxRate : null,
      invoiceSubtotal: (this.invoiceItem.invoiceSubtotal) ? this.invoiceItem.invoiceSubtotal : 0.00,
      invoiceDescription: (this.invoiceItem.description) ? this.invoiceItem.description : null
    });
    this.salesTaxAmount = this.invoiceItem.invoiceTaxAmount;
    this.unitPrice = this.invoiceItem.unitPrice;
    this.invoiceSubtotal = this.invoiceItem.invoiceSubtotal;
    this.invoiceTotal = this.invoiceItem.invoiceTotal;
    this.invoiceType = this.invoiceItem.invoiceType;

    if (this.invoiceItem.invoiceType === 'Order') {
      this.hourly = (this.invoiceItem.unitPrice !== null) ? true : false;
    }

    if (this.invoiceType === 'Material') {
      const product = this.products.find(x => x.id === this.invoiceItem.productId);
      this.invoiceForm.addControl('product', new FormControl(product, Validators.required));
    } else {
      const service = this.acctServices.find(x => x.id === this.invoiceItem.serviceId);
      this.invoiceForm.addControl('service', new FormControl(service, Validators.required));
    }
  }


  notZeroValidator(control: AbstractControl) {
    // Ensure that if the user is supposed to enter a value that it is not
    // less than 0
    if (control.value) {
      if (parseFloat(control.value) <= 0) {
        return { required: true };
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  initForm() {
    this.invoiceForm = this.fb.group({
      salesTaxRate: [this.invoiceItem.salesTaxRate, Validators.required],
      quantity: [1, [Validators.required, Validators.min(-1000), Validators.max(1000)]],
      unitPrice: [0.00, [Validators.required, Validators.min(0), Validators.max(100000)]],
      invoiceSubtotal: [0.00],
      invoiceDescription: ['', [Validators.required, Validators.maxLength(60)]]
    });
    if (this.editMode) {
      console.log(this.invoiceItem);
      this.patchForm();
    } else {
      this.newItemControls();
    }
    this.onValueChanges();
  }

  onValueChanges() {
    this.invoiceForm.get('quantity').valueChanges
      .pipe(
        takeUntil(this.unsubscribe$),
        debounceTime(500)
      )
      .subscribe(() => {
        this.calculateAmounts();
      });


    this.invoiceForm.get('salesTaxRate').valueChanges
      .pipe(
        takeUntil(this.unsubscribe$),
        debounceTime(500)
      )
      .subscribe(() => {
        this.calculateAmounts();
      })


    this.invoiceForm.get('unitPrice').valueChanges
      .pipe(
        takeUntil(this.unsubscribe$),
        debounceTime(500)
      )
      .subscribe(() => {
        this.calculateAmounts();
      })
  }

  onInvoiceTypeChanges() {
    this.invoiceForm.get('invoiceType').valueChanges
      .subscribe(val => {
        this.invoiceType = val;
        if (this.invoiceType === 'Material') {
          this.invoiceForm.removeControl('service');
          this.invoiceForm.addControl('product', new FormControl(null, Validators.required));
        } else {
          this.invoiceForm.removeControl('product');
          this.invoiceForm.addControl('service', new FormControl(null, Validators.required));
        }
      });
  }

  calculateAmounts() {
    // If the user changes a field, recalculate everything and patch the form
    if (this.invoiceForm.value.salesTaxRate !== null) {
      const quantity : number = this.invoiceForm.value.quantity
      const unitPrice : number = this.invoiceForm.value.unitPrice
      this.invoiceSubtotal = (quantity * unitPrice);
      this.salesTaxAmount = this.invoiceSubtotal * (this.invoiceForm.value.salesTaxRate.rate / 100);
      this.invoiceTotal = this.invoiceSubtotal + (this.invoiceSubtotal * (this.invoiceForm.value.salesTaxRate.rate / 100));
      this.invoiceForm.patchValue({
        invoiceSubtotal: this.invoiceSubtotal.toFixed(2)
      });
    }
  }


  onSubmit() {
    if (this.invoiceForm.invalid || (!this.invoiceTotal)) {
      this.snackBar.open('Please fill required fields', 'Error', {
        duration: 5000
      });
      return;
    }

    if (this.editMode) {
      const invoiceItem: InvoiceItemInterfaceWithId = {
        locationId: (this.invoiceItem.locationId) ? this.invoiceItem.locationId : null,
        projectId: (this.invoiceItem.projectId) ? this.invoiceItem.projectId : null,
        orderId: (this.invoiceItem.orderId) ? this.invoiceItem.orderId : null,
        requestId: (this.invoiceItem.requestId) ? this.invoiceItem.requestId : null,
        subscriptionId: (this.invoiceItem.subscriptionId) ? this.invoiceItem.subscriptionId : null,
        invoiceType: this.invoiceItem.invoiceType,
        quantity: parseFloat(this.invoiceForm.value.quantity),
        unitPrice: parseFloat(this.invoiceForm.value.unitPrice),
        salesTaxRate: this.invoiceForm.value.salesTaxRate,
        serviceId: (this.invoiceType !== 'Material') ? this.invoiceForm.value.service.id : null,
        productId: (this.invoiceType === 'Material') ? this.invoiceForm.value.product.id : null,
        invoiceTaxAmount: this.salesTaxAmount,
        invoiceTotal: this.invoiceTotal,
        invoiceSubtotal: this.invoiceSubtotal,
        billingStartDate: this.invoiceItem.billingStartDate,
        billingEndDate: this.invoiceItem.billingEndDate,
        invoiceId: this.invoiceItem.invoiceId,
        status: this.invoiceItem.status,
        description: (this.invoiceForm.value.invoiceDescription) ? this.invoiceForm.value.invoiceDescription : this.invoiceItem.description,
        id: this.invoiceItem.id,
        exists: this.invoiceItem.exists
      };
      this.dialogRef.close(invoiceItem);
    } else {
      const invoiceItem: InvoiceItemInterface = {
        locationId: this.invoiceItem.locationId,
        projectId: this.invoiceForm.value.project.id,
        orderId: null,
        requestId: null,
        subscriptionId: null,
        invoiceType: this.invoiceForm.value.invoiceType,
        quantity: parseFloat(this.invoiceForm.value.quantity),
        unitPrice: parseFloat(this.invoiceForm.value.unitPrice),
        salesTaxRate: this.invoiceForm.value.salesTaxRate,
        serviceId: (this.invoiceType !== 'Material') ? this.invoiceForm.value.service.id : null,
        productId: (this.invoiceType === 'Material') ? this.invoiceForm.value.product.id : null,
        invoiceTaxAmount: this.salesTaxAmount,
        invoiceTotal: this.invoiceTotal,
        invoiceSubtotal: this.invoiceSubtotal,
        billingStartDate: firebase.firestore.Timestamp.fromDate(this.invoiceForm.value.invoiceDate.toDate()),
        billingEndDate: firebase.firestore.Timestamp.fromDate(this.invoiceForm.value.invoiceDate.toDate()),
        invoiceId: this.invoiceItem.invoiceId,
        description: this.invoiceForm.value.invoiceDescription,
        status: 'active'
      };
      this.dialogRef.close(invoiceItem);
    }
  }

  onCancel() {
    this.dialogRef.close();
  }

  filterProjects() {
    this.filteredProjects = this.invoiceForm.get('project').valueChanges
      .pipe(
        startWith<string | ProjectInterfaceWithId>(''),
        map(value => typeof value === 'string' ? value : value.name),
        map(name => name ? this._filteredProjects(name) : this.projects.slice())
      );
  }

  _filteredProjects(value: string): ProjectInterfaceWithId[] {
    const filterValue = value.toLowerCase();
    return this.projects.filter(project => project.name.toLowerCase().indexOf(filterValue) === 0);
  }

  displayProject(project: ProjectInterfaceWithId): string | undefined {
    return project ? project.name : undefined;
  }

  // Get the lists of services and products for the selects
  getData() {
    this.acctServicesService.getServiceSource()
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap(data => {
          if (data && (Object.keys(data).length !== 0)) {
            this.acctServices = this.acctServicesService.getServiceList();
          }
          return this.suppliesService.getProductSource();
        }),
        switchMap(productData => {
          if (productData && (Object.keys(productData).length !== 0)) {
            this.products = this.suppliesService.getProductList();
          }
          return this.projectsFacade.allProjectsDictionary$;
        }),
        switchMap(projectData => {
          if (projectData && (Object.keys(projectData).length !== 0)) {
            this.projectLookup = projectData;
          }
          return this.projectsFacade.allProjects$;
        })
      )
      .subscribe(projectList => {
          if (projectList && projectList.length) {
            this.projects = projectList;
            this.onReady = true;
          }
        },
        error => {
          if (!environment.production) {
            console.log('error getting staff and locations' + error);
          }
        });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
